import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Landmark, LogOut, ChevronRight, Clock } from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { TEMPLE_BG } from "../lib/assets";
import { istLongDate } from "../lib/istDate";
import DownloadAppButton from "./DownloadAppButton";

const ROLE_LABEL = { SUPER_ADMIN: "Super Admin", TEMPLE_ADMIN: "Temple Admin", SUPERVISOR: "Supervisor", COUNTER_USER: "Counter User", VERIFICATION_USER: "Verification" };
// Icon bubble colour per module, so each group is easy to spot (same colour for every user).
const TONES = {
  Home: "from-[#C3122A] to-[#F38B1C]", Protocol: "from-[#C3122A] to-[#F38B1C]", Sarees: "from-[#7C3AED] to-[#C084FC]",
  Annaprasadam: "from-[#0F766E] to-[#34D399]", "Identity & Passes": "from-[#1D4ED8] to-[#60A5FA]",
  Hundial: "from-[#B45309] to-[#FBBF24]", Administration: "from-[#475569] to-[#94A3B8]",
};

// Phone home screen (and the Android app's start screen): temple banner with who is signed in and
// today's work, then the permitted pages as app-style tiles grouped by module.
export default function MobileHome({ items, user, logo, templeName, templeTelugu }) {
  const navigate = useNavigate();
  const { logout } = useAuth();
  const [today, setToday] = useState(null);

  useEffect(() => { api.get("/me/today").then((r) => setToday(r.data)).catch(() => {}); }, []);

  // Group the pages under their sidebar sections; Children Tag opens straight into Assign Tag on the phone.
  const groups = items.reduce((acc, i) => {
    if (i.section) acc.push({ title: i.section, tiles: [] });
    else {
      if (!acc.length) acc.push({ title: "Home", tiles: [] });
      acc[acc.length - 1].tiles.push(i.page === "ID_CHILDREN" ? { ...i, to: "/identity/assign-tag", label: "Assign Tag" } : i);
    }
    return acc;
  }, []).filter((g) => g.tiles.length);

  const stats = today ? [
    { label: "Booked", value: today.booked, show: today.booked || user.role === "COUNTER_USER" },
    { label: "Issued", value: today.issued, show: today.issued || user.role === "COUNTER_USER" },
    { label: "Verified", value: today.verified, show: today.verified || user.role === "VERIFICATION_USER" },
    { label: "Saree tokens", value: today.saree_tokens, show: today.saree_tokens > 0 },
  ].filter((s) => s.show) : [];

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto bg-[#F6F1E8]" data-testid="mobile-home">
      <div className="min-h-full flex flex-col">
      {/* Banner */}
      <div className="relative text-white overflow-hidden rounded-b-[2rem] shadow-xl">
        <img src={TEMPLE_BG} alt="" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-br from-[#5B0A1C]/95 via-[#8E0F2A]/90 to-[#C2410C]/85" />
        <div className="relative px-5 pt-5 pb-6">
          <div className="flex items-center gap-3">
            <div className="w-14 h-14 rounded-2xl bg-white shadow-lg flex items-center justify-center shrink-0">
              {logo ? <img src={logo} alt="logo" className="w-12 h-12 object-contain" /> : <Landmark className="w-7 h-7 text-maroon-800" />}
            </div>
            <div className="min-w-0 flex-1">
              {templeTelugu && <p className="font-telugu text-lg text-gold-300 leading-tight truncate">{templeTelugu}</p>}
              <h1 className="font-semibold text-[17px] leading-snug" data-testid="mobile-temple-name">{templeName || "Temple Management System"}</h1>
            </div>
            <button type="button" onClick={() => { logout(); navigate("/login"); }} data-testid="mobile-logout"
              className="w-11 h-11 rounded-xl bg-white/15 border border-white/25 flex items-center justify-center active:scale-95 shrink-0" aria-label="Log out">
              <LogOut className="w-5 h-5" />
            </button>
          </div>

          <div className="mt-5 rounded-2xl bg-white/10 border border-white/20 backdrop-blur-md px-4 py-3">
            <div className="flex items-center gap-3">
              <span className="w-10 h-10 rounded-full bg-gold-400 text-maroon-900 font-bold flex items-center justify-center shrink-0">
                {(user.full_name || "?").trim().charAt(0).toUpperCase()}
              </span>
              <div className="min-w-0 flex-1">
                <p className="font-semibold leading-tight truncate" data-testid="mobile-user-name">{user.full_name}</p>
                <p className="text-xs text-white/80 truncate">{ROLE_LABEL[user.role] || user.role}{user.temple_id ? ` · ${user.temple_id}` : ""}</p>
              </div>
              {user.shift_name && (
                <span className="text-[11px] font-semibold bg-gold-400/25 border border-gold-300/50 rounded-full px-2.5 py-1 flex items-center gap-1 shrink-0" data-testid="mobile-shift">
                  <Clock className="w-3 h-3" />{user.shift_start ? `${user.shift_start}–${user.shift_end}` : user.shift_name}
                </span>
              )}
            </div>
            <p className="text-[11px] text-white/70 mt-2">{istLongDate()}</p>
          </div>

          {stats.length > 0 && (
            <div className={`mt-3 grid gap-2 ${stats.length >= 3 ? "grid-cols-3" : stats.length === 2 ? "grid-cols-2" : "grid-cols-1"}`} data-testid="mobile-today">
              {stats.slice(0, 3).map((s) => (
                <div key={s.label} className="rounded-xl bg-white text-center py-2 shadow">
                  <p className="text-xl font-extrabold text-maroon-800 leading-tight">{s.value || 0}</p>
                  <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">Today · {s.label}</p>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Tiles */}
      <div className="px-4 pt-5 pb-4 space-y-5 max-w-md mx-auto">
        {groups.map((g, gi) => (
          <section key={g.title + gi}>
            <p className="px-1 mb-2 text-[11px] font-bold uppercase tracking-[0.18em] text-gold-700">{g.title}</p>
            <div className="grid grid-cols-3 gap-2.5">
              {g.tiles.map((i) => (
                <button key={i.to + i.label} type="button" onClick={() => navigate(i.to === "/" ? "/dashboard" : i.to)} data-testid={`mobile-tile-${i.to.replace(/\//g, "-")}`}
                  className="rounded-2xl bg-white border border-[#EADFCF] shadow-sm px-1.5 pt-3 pb-2.5 flex flex-col items-center gap-2 active:scale-95 transition">
                  <span className={`w-11 h-11 rounded-xl bg-gradient-to-br ${TONES[g.title] || TONES.Protocol} text-white flex items-center justify-center shadow`}>
                    <i.icon className="w-5 h-5" />
                  </span>
                  <span className="text-[11.5px] font-semibold text-slate-700 leading-tight text-center line-clamp-2">{i.label}</span>
                </button>
              ))}
            </div>
          </section>
        ))}
        {!groups.length && (
          <div className="rounded-2xl bg-white border border-[#EADFCF] p-5 text-center text-sm text-slate-500 flex items-center justify-center gap-1">
            No pages are assigned to you yet. Please contact the administrator. <ChevronRight className="w-4 h-4" />
          </div>
        )}
      </div>

      {/* Footer: always at the bottom of the screen, even with only a few tiles */}
      <div className="mt-auto pb-6 pt-6 text-center" data-testid="mobile-footer">
        <DownloadAppButton className="px-4 py-2" />
        <img src="/logo-192.png" alt="Aatreya" className="w-8 h-8 mx-auto mt-5 object-contain opacity-80" />
        <p className="text-[11px] text-slate-500 mt-1">© {new Date().getFullYear()} Aatreya Infotech · Temple Management System</p>
      </div>
      </div>
    </div>
  );
}
